import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useCallback } from "react"
import {
	CouponForm,
	type CouponFormValues,
	centsToDollars,
} from "@/components/coupons/coupon-form"
import { Skeleton } from "@/components/ui/skeleton"
import { useAdminCoupon, useCreateCoupon } from "@/queries/marketplace"

export const Route = createFileRoute("/coupons/new")({
	component: CreateCouponPage,
	validateSearch: (search: Record<string, unknown>) => ({
		from: search.from as string | undefined,
	}),
})

function CreateCouponPage() {
	const navigate = useNavigate()
	const searchParams = useSearch({ from: "/coupons/new" })
	const createMutation = useCreateCoupon()

	// If duplicating from an existing coupon, fetch its data
	const { coupon: sourceCoupon, isLoading: isLoadingSource } = useAdminCoupon({
		couponId: searchParams.from ?? "",
	})

	const handleSubmit = useCallback(
		async (values: CouponFormValues) => {
			const result = await createMutation.mutateAsync({
				code: values.code,
				description: values.description,
				type: values.type,
				value: values.value,
				currency: values.currency,
				region: values.region,
				applicableCurrencies: values.applicableCurrencies,
				currencyAmounts: values.currencyAmounts,
				maxRedemptions: values.maxRedemptions,
				maxRedemptionsPerUser: values.maxRedemptionsPerUser,
				minimumOrderAmount: values.minimumOrderAmount,
				maximumDiscountAmount: values.maximumDiscountAmount,
				startsAt: values.startsAt.toISOString(),
				expiresAt: values.expiresAt?.toISOString() ?? null,
				newBuyersOnly: values.newBuyersOnly,
				accountMustPredateCoupon: values.accountMustPredateCoupon,
				audience: values.audience,
			})
			navigate({
				to: "/coupons/$couponId",
				params: { couponId: result.id },
				search:
					result.audience === "restricted"
						? { tab: "eligible" as const, addUsers: true as const }
						: {},
			})
		},
		[createMutation, navigate],
	)

	// Show loading while fetching source coupon for duplication
	if (searchParams.from && isLoadingSource) {
		return (
			<div className="flex flex-col gap-6 p-6">
				<Skeleton className="h-8 w-64" />
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<Skeleton className="h-64" />
					<Skeleton className="h-64" />
				</div>
			</div>
		)
	}

	// Build initial values from source coupon (for duplication).
	// Convert API values (cents, ISO strings) to form values (dollars, Dates).
	const initialValues: Partial<CouponFormValues> | undefined = sourceCoupon
		? {
				description: sourceCoupon.description,
				type: sourceCoupon.type,
				value:
					sourceCoupon.type === "fixed_amount"
						? sourceCoupon.value / 100
						: sourceCoupon.value,
				currency: sourceCoupon.currency,
				region: sourceCoupon.region,
				applicableCurrencies: sourceCoupon.applicableCurrencies,
				currencyAmounts: (sourceCoupon.currencyAmounts ?? []).map((ca, i) => ({
					_key: `dup_${i}`,
					currency: ca.currency,
					amount: ca.amount / 100,
					minimumOrderAmount: centsToDollars(ca.minimumOrderAmount ?? null),
					maximumDiscountAmount: centsToDollars(
						ca.maximumDiscountAmount ?? null,
					),
				})),
				maxRedemptions: sourceCoupon.maxRedemptions,
				maxRedemptionsPerUser: sourceCoupon.maxRedemptionsPerUser,
				minimumOrderAmount: centsToDollars(sourceCoupon.minimumOrderAmount),
				maximumDiscountAmount: centsToDollars(
					sourceCoupon.maximumDiscountAmount,
				),
				startsAt: new Date(sourceCoupon.startsAt),
				expiresAt: sourceCoupon.expiresAt
					? new Date(sourceCoupon.expiresAt)
					: null,
				newBuyersOnly: sourceCoupon.newBuyersOnly,
				accountMustPredateCoupon: sourceCoupon.accountMustPredateCoupon,
				audience: sourceCoupon.audience,
				// Intentionally omit code — user must enter a new one
			}
		: undefined

	return (
		<div className="p-6">
			<CouponForm
				mode="create"
				initialValues={initialValues}
				onSubmit={handleSubmit}
				isSubmitting={createMutation.isPending}
			/>
		</div>
	)
}
